import { useState, useMemo } from 'react';

interface LineUser {
  line_display_name?: string;
  custom_display_name?: string;
  last_message_content?: string;
  line_user_id: string;
  is_pinned?: boolean;
  linked_reservations?: any[];
  reservation_ids?: (string | number)[];
  reservation_statuses?: string[];
  instance_res_data?: {
    start_date?: string;
    end_date?: string;
    pickup_date?: string;
    return_date?: string;
    customer_name?: string;
    contact_code?: string;
  };
  tags?: { id: string | number }[];
  [key: string]: any;
}

interface CustomDateFilter {
  date: string;
  type: 'pickup' | 'return';
}

export const useLineFilters = (
    users: LineUser[],
    initialFilters: string[] = [],
    tagFilters: (string | number)[] = [],
    serverMatchedUids: Set<string> = new Set()
) => {
    const [filters, setFilters] = useState<string[]>(initialFilters);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [customDateFilter, setCustomDateFilter] = useState<CustomDateFilter | null>(null);

    const toggleFilter = (filterId: string) => {
        if (filterId === 'all') {
            setFilters([]);
            setSearchQuery('');
            setCustomDateFilter(null);
            return;
        }
        setFilters(prev =>
            prev.includes(filterId)
                ? prev.filter(f => f !== filterId)
                : [...prev, filterId]
        );
    };

    const filteredUsers = useMemo(() => {
        let result = users;

        // 1. Search Query
        //    クライアント可視フィールド（表示名・LINE名・予約顧客名・連絡コード・
        //    予約番号・直近メッセージ）でマッチ。加えて、サーバー検索がヒットした
        //    ユーザー（メッセージ本文全文・アーカイブ等、クライアントが持たない情報で
        //    マッチしたもの）は serverMatchedUids 経由で必ず通す。
        //    → どちらか一方でもマッチすれば表示（ユニオン）。二重フィルタによる
        //      取りこぼしを防ぐ。
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(u => {
                if (serverMatchedUids.has(u.line_user_id)) return true;

                const res = u.instance_res_data as any;
                const resCustomerName = res?.customer_name as string | undefined;
                const resContactCode = res?.contact_code as string | undefined;
                const resIds = (u.reservation_ids || []).map(r => String(r));

                return (
                    (u.line_display_name && u.line_display_name.toLowerCase().includes(query)) ||
                    (u.custom_display_name && u.custom_display_name.toLowerCase().includes(query)) ||
                    (resCustomerName && resCustomerName.toLowerCase().includes(query)) ||
                    (resContactCode && resContactCode.toLowerCase().includes(query)) ||
                    resIds.some(rid => rid.toLowerCase().includes(query)) ||
                    (u.last_message_content && u.last_message_content.toLowerCase().includes(query)) ||
                    u.line_user_id.toLowerCase().includes(query)
                );
            });
        }

        // 2. Custom Date Filter
        if (customDateFilter) {
            const { date, type } = customDateFilter;
            result = result.filter(u => {
                const res = u.instance_res_data;
                const targetDate = type === 'pickup' ? (res?.start_date || res?.pickup_date) : (res?.end_date || res?.return_date);
                return targetDate && targetDate.startsWith(date);
            });
        }

        // 3. Status Filters (Chip-based)
        if (filters.length > 0) {
            result = result.filter(u => {
                const match = filters.some(f => {
                    if (f === 'pinned') return u.is_pinned;
                    // 未読（2026-10-26・customer-chat.md §13-6）: カードに赤い数字や「新着」が付いている会話。LINE は unread_count、
                    // 自社チャットは toReservationChatInstance / toAnonChatInstance が未読なら 1 を入れている
                    if (f === 'unread') return Number(u.unread_count || 0) > 0;
                    if (f === 'unlinked') return !u.linked_reservations || u.linked_reservations.length === 0;
                    if (f === 'today_pickup') return u.reservation_statuses?.includes('本日貸出');
                    if (f === 'today_return') return u.reservation_statuses?.includes('本日返却');
                    if (f === 'tomorrow_pickup') return u.reservation_statuses?.includes('明日貸出');
                    if (f === 'tomorrow_return') return u.reservation_statuses?.includes('明日返却');
                    if (f === 'in_use') {
                        return u.reservation_statuses?.includes('利用中');
                    }
                    return false;
                });
                return match;
            });
        }

        // 4. Tag Filters
        if (tagFilters.length > 0) {
            result = result.filter(u => {
                const userTagIds = (u.tags || []).map(t => t.id);
                return tagFilters.some(tid => userTagIds.includes(tid));
            });
        }

        return result;
    }, [users, filters, searchQuery, customDateFilter, tagFilters, serverMatchedUids]);

    return {
        filters,
        toggleFilter,
        searchQuery,
        setSearchQuery,
        customDateFilter,
        setCustomDateFilter,
        filteredUsers
    };
};
